import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@ucs/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useEffect, useLayoutEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    alphabetContainer?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    galleryContainer?: InjectableCss;
    imageItem?: InjectableCss;
    link?: LinkProps;
    image?: LazyImageProps;
}

export const listStyles: BrandListStyles = {
    container: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 18px;
                    `,
                    css`
                        margin-top: 18px;
                    `,
                    css`
                        margin-top: 35px;
                    `,
                    css`
                        margin-top: 50px;
                    `,
                    css`
                        margin-top: 50px;
                    `,
                ])};
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin: 10px;
        `,
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    alphabetContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: baseline;
            padding: 0;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 0 0 calc((100% - 335px) / 2);
                    `,
                    css`
                        padding: 0 0 0 20px;
                    `,
                    css`
                        padding: 0 10px;
                    `,
                    css``,
                    css``,
                ])};
        `,
    },
    galleryContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                        margin: 42px 0;
                    `,
                    css`
                        padding: 0 10px;
                        margin: 42px 0;
                    `,
                    css`
                        padding: 0 40px;
                        margin: 60px 0;
                    `,
                    css`
                        padding: 0 80px;
                        margin: 70px 0;
                    `,
                    css`
                        padding: 0 110px;
                        margin: 80px 0;
                    `,
                ])};
        `,
    },
    imageItem: {
        css: css`
            width: fit-content;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 5px;
                    `,
                    css`
                        margin: 6px;
                    `,
                    css`
                        margin: 10px;
                    `,
                    css`
                        margin: 15px;
                    `,
                    css`
                        margin: 20px;
                    `,
                ])};
        `,
    },
    link: {
        css: css`
            background-color: ${UCSColors.gray10};
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 6px;
                    `,
                    css`
                        padding: 6px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 16px;
                    `,
                    css`
                        padding: 20px;
                    `,
                ])};
        `,
    },
};

const styles = listStyles;

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState } = props;

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const { brandLetterDetails } = buildBrandSections(brandAlphabet);

    const [selectedElement, setSelectedElement] = useState("");
    const [galleryList, setGalleryList] = useState(brandList);
    const [lastLetterClicked, setlastLetterClicked] = React.useState("");

    useEffect(() => {
        if (brandList !== galleryList) {
            setGalleryList(brandList);
        }
    }, [allBrandsDataView.value]);

    useLayoutEffect(() => {
        if (lastLetterClicked) {
            const letterElement = document.getElementById(`letter-${lastLetterClicked}`);
            if (letterElement) {
                const letterOffset = letterElement.offsetTop;
                if (letterOffset > 0) {
                    window.scrollTo(0, letterOffset);
                }
            }
            setlastLetterClicked("");
        }
    }, [lastLetterClicked]);

    const imgProps = {
        style: {
            width: "150px",
            maxWidth: "150px",
            maxHeight: `150px`,
        },
    };

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    // const handleBackToTop = () => {
    //     window.scrollTo(0, 0);
    // };

    const handleBrandLetterClick = (letter: string) => {
        const items = brandList.filter(item => item.name[0].toUpperCase() === letter.toUpperCase());
        items && setGalleryList(items);
        setSelectedElement(letter);
        setlastLetterClicked(letter || "");
    };

    const handleShowAllClick = () => {
        setGalleryList(brandList);
        setSelectedElement("");
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            <StyledWrapper {...styles.alphabetContainer}>
                <Button
                    variant={selectedElement === "" ? "primary" : "secondary"}
                    sizeVariant="small"
                    shape="pill"
                    style={{ textTransform: "uppercase" }}
                    data-test-selector="brandListCollapseAllLink"
                    onClick={handleShowAllClick}
                >
                    {translate("All")}
                </Button>
                <BrandAlphabetNavigation
                    letterDetails={brandLetterDetails}
                    extendedStyles={styles.brandAlphabetNavigation}
                    onBrandLetterClick={handleBrandLetterClick}
                    selectedElement={selectedElement}
                />
            </StyledWrapper>

            <StyledWrapper {...styles.galleryContainer}>
                {galleryList.map(brand => (
                    <StyledWrapper key={brand.id} {...styles.imageItem}>
                        <Clickable href={brand.detailPagePath} {...styles.link}>
                            <LazyImage
                                imgProps={imgProps}
                                {...styles.image}
                                src={brand.logoSmallImagePath}
                                altText={brand.logoAltText}
                            />
                        </Clickable>
                    </StyledWrapper>
                ))}
            </StyledWrapper>

            {/* <StyledWrapper {...styles.buttons}>
                <Button {...styles.backToTopButton} onClick={handleBackToTop}>
                    {translate("Back to Top")}
                </Button>
            </StyledWrapper> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param brandAlphabet Letter count list.
 */
const buildBrandSections = (
    brandAlphabet: BrandAlphabetLetterModel[],
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));
    return { brandLetterDetails };
};
